import React, { useState, useContext, useEffect } from "react"
import { useQuery } from "@apollo/client"
import _ from "lodash"

import StaffAssignedParticipants from "./StaffAssignedParticipants"
import StaffProfilePhoto from "./StaffProfilePhoto"
import { GET_STAFF_ID_BY_MEMBERSHIP_ID, GET_STAFF_PROFILE_BOX } from "../queries"
import { LoaderSmall } from "../../../components/Loaders"
import { UserContext } from "../../../context/userContext"
import { FirebaseContext } from "../../../firebase"

// Custom hook for fetching staff data
const useStaffData = (staff_membership_id, firebase, userCtx) => {
  const [currentStaffData, setCurrentStaffData] = useState(null)
  const [telerecoveryEnabled, setTelerecoveryEnabled] = useState(false)

  const { data: staffData, loading: staffLoading } = useQuery(
    GET_STAFF_ID_BY_MEMBERSHIP_ID,
    {
      variables: { staff_membership_id },
      skip: !staff_membership_id,
      fetchPolicy: "network-only",
    }
  )

  const { loading } = useQuery(GET_STAFF_PROFILE_BOX, {
    variables: {
      organization_id: staffData?.staffDataByStaffMembershipID?.organization_id,
      staff_id: staffData?.staffDataByStaffMembershipID?.staff_id,
    },
    fetchPolicy: "no-cache",
    skip:
      !staffData?.staffDataByStaffMembershipID?.staff_id ||
      !staffData?.staffDataByStaffMembershipID?.organization_id,
    onCompleted: (data) => {
      setCurrentStaffData(data.getStaffProfileBox || [])
    },
  })

  useEffect(() => {
    const microservicesRef = firebase.db
      .collection("enterprise-config")
      .doc(userCtx.session.user.enterprise_id)
      .collection("organizations")
      .doc(userCtx.session.user.organization_id)
      .collection("microservices")

    const unsubscribe = microservicesRef.onSnapshot(
      (snapshot) => {
        if (!snapshot.empty) {
          const microservicesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          setTelerecoveryEnabled(
            microservicesData.find((service) => service.id === "1")?.enable
          )
        } else {
          console.log("No microservices found!")
        }
      },
      (error) => {
        console.log("Error listening to microservices collection:", error)
      }
    )

    return () => unsubscribe()
  }, [firebase.db, userCtx.session.user.enterprise_id, userCtx.session.user.organization_id])

  return { currentStaffData, telerecoveryEnabled, loading, staffLoading }
}

export default function ParticipantProfileBoxSmall() {
  const userCtx = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const staff_membership_id = userCtx.current_staff_membership_id

  const { currentStaffData, telerecoveryEnabled, loading, staffLoading } = useStaffData(
    staff_membership_id,
    firebase,
    userCtx
  )

  if (loading || staffLoading) return <LoaderSmall />

  return (
    <div className="profile-content">
      <div className="profile-section nav">
        <StaffAssignedParticipants
          assignedParticipants={currentStaffData?.assigned_participants}
        />

        <div className="participant-information">
          <div>
            <span>Staff Member ID:</span> {userCtx.current_staff_membership_id}
          </div>
          <div>
            <span>Staff Role:</span>{" "}
            {_.startCase(currentStaffData?.staff_role_name?.replace(/_/g, "-"))}
          </div>
          <div>
            <span>Supervisor:</span> {currentStaffData?.staff_primary_supervisor_name}
          </div>
          <div>
            <span>Hire Date:</span> {currentStaffData?.staff_hire_date}
          </div>
          <div>
            <span>Staff Phone: </span> {currentStaffData?.staff_phone_number}
          </div>
          {telerecoveryEnabled && (
            <div>
              <span>Telerecovery Phone: </span>{" "}
              {currentStaffData?.staff_phone_telerecovery}
            </div>
          )}
          <div>
            <span>Last Supervision Date:</span>{" "}
            {currentStaffData?.staff_last_supervision_date
              ? new Date(currentStaffData.staff_last_supervision_date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
              : "N/A"}
          </div>
          <div>
            <span>Last Login Date: </span>
            {currentStaffData?.staff_last_login || "N/A"}
          </div>
        </div>
        <div className="profile-participant">
          <h2>
            {currentStaffData?.staff_name_first +
              " " +
              currentStaffData?.staff_name_last}
          </h2>
          <StaffProfilePhoto staff_profile_image={currentStaffData?.staff_profile_image} />
        </div>
      </div>
    </div>
  )
}
